/**
 * Reusable Allocation Child List Item Component
 * Displays an allocated child with avatar, name, internal ID, age badge, and services
 * Used in both Day View (GroupCard) and Week View Modal (CalendarCell)
 */

import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/macro';
import { format, parseISO } from 'date-fns';
import { ArrowLeftRight, Star, LogIn, LogOut, MoreVertical, GripVertical } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatChildAge } from '@/utils/formatChildAge';
import { getDateFnsLocale } from '@/utils/localeUtils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useChild } from '@/hooks/useChildren';
import { useImageUrl } from '@/hooks/useImage';
import { isImageId, isDirectImageUrl } from '@/utils/imageUtils';
import { getInitials, cn } from '@/lib/utils';
import { formatServiceList } from '@/lib/serviceTranslations';
import { EntityLabels } from '@/components/ui/entity-labels';
import { useEntityLabels } from '@/hooks/settings/useLabels';
import type { Service } from '@/types/billing';

interface ChildData {
  firstname: string;
  lastname: string;
  avatarUrl?: string;
  childInternalId?: string;
}

interface AllocationChildListItemProps {
  childId: string;
  services: string[];
  serviceDefinitions: Service[];
  ageTypeCode: string;
  isAbsent?: boolean;
  isExtraDaycare?: boolean;
  hasOverride?: boolean;
  absenceReason?: string;
  absenceIsFullDay?: boolean;
  onClick?: () => void;
  childData?: ChildData; // Optional pre-fetched child data to avoid N+1 queries
  birthdate?: string; // YYYY-MM-DD format
  agesOutOn?: string; // YYYY-MM-DD: first day the child's age exceeds the group's upper band
  referenceDate?: string; // ISO date string for the viewed date (for age calculation)
  allocationFromDate?: string; // YYYY-MM-DD format
  allocationToDate?: string; // YYYY-MM-DD format
  isJoiningThisDay?: boolean;
  isLeavingThisDay?: boolean;
  /**
   * CAP-4c / C18: opens the single-child move flow. Additive — when omitted the
   * `⋮` menu is not rendered and the chip behaves exactly as before.
   */
  onMoveRequest?: (childId: string, currentGroupId?: string) => void;
  /** The group this chip currently sits in, forwarded to the move flow. */
  currentGroupId?: string;
  /**
   * CAP-4c / C18 — makes the chip a drag source. The listeners sit on a
   * dedicated grip affordance, never on the whole row, so the existing row
   * click and the `⋮` menu keep working. Every drag outcome is also reachable
   * through the `⋮` menu, which ships independently of this flag.
   */
  draggable?: boolean;
}

export function AllocationChildListItem({
  childId,
  services,
  serviceDefinitions,
  ageTypeCode,
  isAbsent,
  isExtraDaycare,
  hasOverride,
  absenceReason,
  absenceIsFullDay,
  onClick,
  childData,
  birthdate,
  agesOutOn,
  referenceDate,
  allocationFromDate,
  allocationToDate,
  isJoiningThisDay,
  isLeavingThisDay,
  onMoveRequest,
  currentGroupId,
  draggable = false,
}: AllocationChildListItemProps) {
  const { i18n } = useLingui();

  // Fetch full child details only if not provided (avoid N+1 queries when pre-fetched)
  const { data: fetchedChildDetails } = useChild(childId, { enabled: !childData });

  // Fetch labels for this child
  const { data: childLabels } = useEntityLabels(childId, 'children');

  // Use provided childData or fallback to fetched data
  const childDetails = childData || fetchedChildDetails;

  // Resolve avatar URL using the standard pattern
  const avatarUrlRaw = childDetails?.avatarUrl;
  const avatarUrl = avatarUrlRaw?.trim() || undefined;
  const needsImageResolution = isImageId(avatarUrl);
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  const finalAvatarUrl = useMemo(() => {
    if (needsImageResolution) {
      return resolvedImageUrl || undefined;
    } else if (isDirectImageUrl(avatarUrl)) {
      return avatarUrl;
    }
    return undefined;
  }, [needsImageResolution, resolvedImageUrl, avatarUrl]);

  // Get child name and details from merged childDetails
  const firstname = childDetails?.firstname || '';
  const lastname = childDetails?.lastname || '';
  const fullName = `${firstname} ${lastname}`.trim();
  const initials = getInitials(fullName) || "??";
  const internalId = childDetails?.childInternalId;

  // The draggable payload carries what the drop handler needs to build a move,
  // plus the name the drop targets announce. Declared after fullName resolves
  // so the payload never carries a stale empty name on the first render.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `child-${childId}`,
    disabled: !draggable,
    data: { childId, currentGroupId, childName: fullName },
  });

  // Format age type label for badge
  const getAgeTypeLabel = (ageType: string) => {
    switch (ageType) {
      case 'BABY':
        return i18n._({ id: 'capacity.allocation.ageType.baby', message: 'Baby' });
      case 'CHILD':
        return i18n._({ id: 'capacity.allocation.ageType.child', message: 'Child' });
      default:
        return ageType;
    }
  };

  const getAgeTypeColor = (ageType: string) => {
    switch (ageType) {
      case 'BABY':
        return 'border-info-on-surface/40 text-info-on-surface';
      case 'CHILD':
        return 'border-success-on-surface/40 text-success-on-surface';
      default:
        return 'border-border text-muted-foreground';
    }
  };

  // Get absence reason label (matching AbsenceCard.tsx pattern)
  const getAbsenceReasonLabel = (reason?: string) => {
    switch (reason?.toUpperCase()) {
      case 'ILLNESS':
        return i18n._({ id: 'capacity.absence.reason.illness', message: 'Illness' });
      case 'VACATION':
        return i18n._({ id: 'capacity.absence.reason.vacation', message: 'Vacation' });
      case 'OTHER':
        return i18n._({ id: 'capacity.absence.reason.other', message: 'Other' });
      default:
        return i18n._({ id: 'capacity.absence.reason.unknown', message: 'Unknown' });
    }
  };

  // Get absence reason color (matching AbsenceCard.tsx pattern)
  const getAbsenceReasonColor = (reason?: string) => {
    switch (reason?.toUpperCase()) {
      case 'ILLNESS':
        return 'border-destructive-on-surface/40 text-destructive-on-surface';
      case 'VACATION':
        return 'border-info-on-surface/40 text-info-on-surface';
      default:
        return 'border-border text-muted-foreground';
    }
  };

  // Compute age string from birthdate
  const ageDisplay = useMemo(() => {
    if (!birthdate) return null;
    return formatChildAge(birthdate, i18n, referenceDate ? new Date(referenceDate) : undefined);
  }, [birthdate, i18n, referenceDate]);

  // "ages out DD.MM" — the day the child's age exceeds the group's upper band (C9). parseISO,
  // never new Date("YYYY-MM-DD") (which would UTC-shift the day).
  const agesOutDisplay = useMemo(() => {
    if (!agesOutOn) return null;
    const parsed = parseISO(agesOutOn);
    return Number.isNaN(parsed.getTime()) ? null : format(parsed, 'dd.MM');
  }, [agesOutOn]);

  // Get locale for date formatting
  const dateLocale = getDateFnsLocale(i18n.locale);

  // Format allocation dates with locale
  const formattedFromDate = useMemo(() => {
    if (!allocationFromDate) return undefined;
    return format(parseISO(allocationFromDate), 'PP', { locale: dateLocale });
  }, [allocationFromDate, dateLocale]);

  const formattedToDate = useMemo(() => {
    if (!allocationToDate) return undefined;
    return format(parseISO(allocationToDate), 'PP', { locale: dateLocale });
  }, [allocationToDate, dateLocale]);

  // The root is a <div>, never a <button>: a DropdownMenu trigger cannot legally
  // or usefully nest inside a button (invalid HTML, and the outer button
  // swallows the inner control's keyboard events). The clickable content is an
  // inner <button> and the `⋮` trigger is its SIBLING. The props and the
  // rendered affordances are unchanged — only the element nesting.
  const ContentWrapper = onClick ? 'button' : 'div';

  return (
    <div
      ref={draggable ? setNodeRef : undefined}
      className={cn(
        "relative w-full flex items-center gap-3 p-2 rounded-lg text-left border border-border",
        onClick && "hover:bg-accent transition-colors",
        isDragging && "opacity-50"
      )}
      data-testid={draggable ? "child-chip-draggable" : undefined}
    >
      {/* A dedicated grip: the drag listeners never cover the whole row, so the
          row click and the ⋮ menu stay operable. */}
      {draggable && (
        <button
          type="button"
          className="flex-shrink-0 cursor-grab touch-none text-muted-foreground"
          data-testid="child-drag-handle"
          aria-label={i18n._({
            id: 'capacity.move.drag.handle',
            message: 'Drag {name} to another group',
            values: { name: fullName },
          })}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
      <ContentWrapper
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        className={cn(
          "flex flex-1 min-w-0 items-center gap-3 text-left",
          onClick && "cursor-pointer"
        )}
      >
      <Avatar className="h-10 w-10 flex-shrink-0">
        {finalAvatarUrl && (
          <AvatarImage src={finalAvatarUrl} alt={fullName} />
        )}
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        {/* First line: Name (ID) and Age Badge */}
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-medium text-sm">
            {fullName}
            {internalId && (
              <span className="text-muted-foreground font-normal"> ({internalId})</span>
            )}
          </span>
          <Badge variant="outline" className={cn("text-xs", getAgeTypeColor(ageTypeCode))}>
            {getAgeTypeLabel(ageTypeCode)}
          </Badge>
          {/* "Ages out DD.MM" — an aging-out child, from the same derivation the attention
              endpoint uses (C9). Carries text + aria-label, never colour alone (WCAG 1.4.1). */}
          {agesOutDisplay && (
            <Badge
              variant="outline"
              className="text-xs border-warning-on-surface/40 text-warning-on-surface"
              data-testid="ages-out-tag"
              aria-label={i18n._({ id: 'capacity.child.agesOut.label', message: 'Ages out of this group on {date}', values: { date: agesOutDisplay } })}
            >
              {i18n._({ id: 'capacity.child.agesOut', message: 'Ages out on {date}', values: { date: agesOutDisplay } })}
            </Badge>
          )}
          {/* Special allocation indicators */}
          {isAbsent && (
            <>
              <Badge variant="secondary" className="text-xs">
                {i18n._({ id: 'capacity.special.type.absence', message: 'Absence' })}
              </Badge>
              <Badge
                variant="outline"
                className={cn("text-xs", getAbsenceReasonColor(absenceReason))}
              >
                {getAbsenceReasonLabel(absenceReason)}
              </Badge>
              {absenceIsFullDay && (
                <Badge variant="outline" className="text-xs">
                  {i18n._({ id: 'capacity.absence.fullDay', message: 'Full Day' })}
                </Badge>
              )}
            </>
          )}
          {isExtraDaycare && (
            <Badge variant="secondary" className="text-xs flex items-center gap-1">
              <Star className="h-3 w-3 fill-current" />
              {i18n._({ id: 'capacity.special.type.extraDay', message: 'Extra Day' })}
            </Badge>
          )}
          {hasOverride && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center px-1.5 py-0.5 rounded bg-warning-muted border border-warning-on-surface/40">
                    <ArrowLeftRight className="h-3 w-3 text-warning-on-surface" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{i18n._({ id: 'capacity.calendar.groupOverride', message: 'Group override' })}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isJoiningThisDay && (
            <Badge className="text-xs" variant="success" aria-label={i18n._({ id: 'capacity.child.joining.label', message: 'Child joining this day' })}>
              <LogIn className="h-3 w-3 mr-1" />
              {i18n._({ id: 'capacity.child.joining', message: 'New' })}
            </Badge>
          )}
          {isLeavingThisDay && (
            <Badge className="text-xs" variant="warning" aria-label={i18n._({ id: 'capacity.child.leaving.label', message: 'Child leaving this day' })}>
              <LogOut className="h-3 w-3 mr-1" />
              {i18n._({ id: 'capacity.child.leaving', message: 'Leaving' })}
            </Badge>
          )}
        </div>
        {/* Labels */}
        <EntityLabels labels={childLabels} size="sm" maxVisible={3} />
        {/* Second line: Age and allocation dates */}
        {(birthdate || allocationFromDate || allocationToDate) && (
          <div className="text-xs text-muted-foreground mb-0.5">
            {ageDisplay && <span>{ageDisplay}</span>}
            {ageDisplay && allocationFromDate && <span className="mx-1">&middot;</span>}
            {formattedFromDate && formattedToDate && (
              <span>{formattedFromDate} → {formattedToDate}</span>
            )}
            {formattedFromDate && !formattedToDate && (
              <span>{i18n._({ id: 'capacity.child.since', message: 'Since {date}', values: { date: formattedFromDate } })}</span>
            )}
            {!formattedFromDate && formattedToDate && (
              <span>{i18n._({ id: 'capacity.child.until', message: 'Until {date}', values: { date: formattedToDate } })}</span>
            )}
          </div>
        )}
        {/* Third line: Services */}
        <div className="text-xs text-muted-foreground">
          <Trans>Services</Trans>: {formatServiceList(services, serviceDefinitions, i18n)}
        </div>
      </div>
      </ContentWrapper>

      {/* C18: the non-drag path. Every drag outcome is reachable here, so no
          drag-only path can exist. Icon-only, so it carries a translated label. */}
      {onMoveRequest && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0"
              data-testid="child-actions-trigger"
              aria-label={i18n._({
                id: 'capacity.child.actions.label',
                message: 'Actions for {name}',
                values: { name: fullName },
              })}
            >
              <MoreVertical className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => onMoveRequest(childId, currentGroupId)}
              data-testid="child-action-move"
            >
              {i18n._({
                id: 'capacity.child.action.moveToGroup',
                message: 'Move to group…',
              })}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
